import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, Search, Pencil, Trash2 } from 'lucide-react';
import { apiClient } from '../apiClient';
import SelectMenu from '../components/SelectMenu';
import VisibilityIndicator from '../components/VisibilityIndicator';
import { richTextToPlainText } from '../utils/richText';
import Modal from '../components/Modal';
import PromptAttachmentList from '../components/PromptAttachmentList';

function SavedPromptsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedSaves, setSelectedSaves] = useState({});
  const [editTarget, setEditTarget] = useState(null);
  const [editName, setEditName] = useState('');
  const [editValues, setEditValues] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const navigate = useNavigate();

  const loadSaved = async () => {
    setLoading(true);
    try {
      const data = await apiClient.get('/api/saved-prompts');
      setGroups(Array.isArray(data) ? data : []);
    } catch (err) {
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSaved();
  }, []);

  const filteredGroups = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return groups;
    return groups.filter((group) =>
      (group.prompt?.name || '').toLowerCase().includes(term)
    );
  }, [groups, search]);

  const handleOpenPrompt = (promptId, savedId) => {
    const state = {
      backTo: '/saved',
      breadcrumbs: [{ label: 'Saved Prompts', to: '/saved' }],
      ...(savedId ? { savedOutputId: savedId } : {})
    };
    navigate(`/prompts/${promptId}`, { state });
  };

  const formatSavedDate = (value) => {
    if (!value) return 'Saved output';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Saved output';
    return date.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  };

  const getSaveLabel = (save) => save?.name || formatSavedDate(save?.created_at);

  const buildVariableList = (prompt, save) => {
    if (prompt?.variables?.length) return prompt.variables;
    const keys = Object.keys(save?.variable_values || {});
    return keys.map((key) => ({
      variable_name: key,
      display_name: key,
      input_type: 'text'
    }));
  };

  const normalizeInputType = (value) => {
    if (!value) return 'text';
    const normalized = String(value).toLowerCase().trim();
    if (normalized === 'multi_line_text') return 'multi_text';
    if (normalized === 'multiline') return 'multi_text';
    if (normalized === 'multiple choice') return 'multiple_choice';
    return normalized;
  };

  const openEdit = (prompt, save) => {
    const variables = buildVariableList(prompt, save).map((variable) => ({
      ...variable,
      input_type: normalizeInputType(variable.input_type)
    }));
    const initialValues = {};
    variables.forEach((variable) => {
      const rawValue = save?.variable_values?.[variable.variable_name];
      if (variable.input_type === 'multiple_choice') {
        if (Array.isArray(rawValue)) {
          initialValues[variable.variable_name] = rawValue;
        } else if (typeof rawValue === 'string') {
          initialValues[variable.variable_name] = rawValue
            .split(',')
            .map((item) => item.trim())
            .filter(Boolean);
        } else {
          initialValues[variable.variable_name] = [];
        }
      } else {
        initialValues[variable.variable_name] = rawValue ?? '';
      }
    });
    setEditTarget({ prompt, save, variables });
    setEditName(save?.name || '');
    setEditValues(initialValues);
    setEditError('');
  };

  const handleUpdateSave = async () => {
    if (!editTarget?.save?.output_id) return;
    setEditSaving(true);
    setEditError('');
    try {
      await apiClient.patch(`/api/outputs/${editTarget.save.output_id}`, {
        name: editName.trim() || null,
        variable_values: editValues
      });
      await loadSaved();
      setEditTarget(null);
    } catch (err) {
      setEditError(err.message || 'Unable to update saved prompt.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteSave = async () => {
    if (!deleteTarget?.output_id) return;
    try {
      await apiClient.del(`/api/outputs/${deleteTarget.output_id}`);
      await loadSaved();
      setDeleteTarget(null);
    } catch (err) {
      // ignore
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold">Saved Prompts</h1>
            <p className="text-gray-400">Reuse your saved inputs instantly.</p>
          </div>
          <div className="search-field">
            <Search className="w-4 h-4" />
            <input
              type="text"
              placeholder="Search saved prompts"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner"></div>
          </div>
        ) : filteredGroups.length === 0 ? (
          <div className="card-glass text-center py-12">
            <p className="text-gray-400 mb-4">No saved prompts yet.</p>
            <button className="btn-primary" onClick={() => navigate('/profile')}>
              Back to Profile
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredGroups.map((group) => {
              const prompt = group.prompt || {};
              const saves = group.saves || [];
              const selectedId = selectedSaves[prompt.prompt_id] || '';
              const options = saves.map((save) => ({
                value: save.output_id,
                label: getSaveLabel(save),
                description: save.name ? formatSavedDate(save.created_at) : 'Saved inputs'
              }));
              const selectedSave = saves.find((save) => save.output_id === selectedId);

              return (
                <div
                  key={prompt.prompt_id}
                  className="card-glass cursor-pointer"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleOpenPrompt(prompt.prompt_id)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleOpenPrompt(prompt.prompt_id);
                    }
                  }}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-lg">{prompt.name}</h3>
                    <div className="flex items-center gap-2">
                      <VisibilityIndicator visibility={prompt.visibility || 'private'} />
                      <span className="badge">{prompt.visibility || 'private'}</span>
                    </div>
                  </div>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {richTextToPlainText(prompt.description) || 'No description'}
                  </p>
                  <div className="saved-card-meta">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <Clock className="w-4 h-4" />
                      {saves[0]?.created_at
                        ? `Last saved ${formatSavedDate(saves[0].created_at)}`
                        : 'No saves'}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="badge">{saves.length} saves</span>
                      {selectedSave?.attachment_count ? (
                        <span className="badge">{selectedSave.attachment_count} files</span>
                      ) : null}
                    </div>
                  </div>
                  <div className="saved-card-actions" onClick={(event) => event.stopPropagation()}>
                    <SelectMenu
                      value={selectedId}
                      options={options}
                      placeholder="Select saved input"
                      onChange={(value) => {
                        setSelectedSaves((prev) => ({ ...prev, [prompt.prompt_id]: value }));
                      }}
                    />
                    <button
                      className="btn-secondary btn-compact"
                      onClick={() => handleOpenPrompt(prompt.prompt_id, selectedId)}
                      disabled={!selectedId}
                    >
                      Use Saved
                    </button>
                    <div className="saved-card-actions-row">
                      <button
                        className="icon-btn"
                        onClick={() => selectedSave && openEdit(prompt, selectedSave)}
                        disabled={!selectedId}
                        data-tooltip="Edit"
                        aria-label="Edit"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="icon-btn danger"
                        onClick={() => selectedSave && setDeleteTarget(selectedSave)}
                        disabled={!selectedId}
                        data-tooltip="Delete"
                        aria-label="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {selectedSave && (
                    <PromptAttachmentList
                      attachments={selectedSave.attachments || []}
                      title="Private Files"
                      emptyLabel="No private files on this saved prompt."
                      compact
                      stopPropagation
                    />
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Modal
        open={Boolean(editTarget)}
        title="Edit Saved Prompt"
        onClose={() => setEditTarget(null)}
        footer={(
          <>
            <button className="btn-secondary" onClick={() => setEditTarget(null)}>Cancel</button>
            <button className="btn-primary" onClick={handleUpdateSave} disabled={editSaving}>
              {editSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </>
        )}
      >
        {editTarget && (
          <div className="space-y-4">
            {editError && (
              <div className="error-banner">{editError}</div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Saved title</label>
              <input
                className="input-glass"
                value={editName}
                onChange={(event) => setEditName(event.target.value)}
                placeholder="e.g., Twitter promo variant"
              />
            </div>
            <div>
              <div className="text-sm font-semibold mb-2">Variable inputs</div>
              <div className="space-y-3">
                {editTarget.variables.map((variable) => {
                  const value = editValues[variable.variable_name];
                  const inputType = normalizeInputType(variable.input_type);
                  const isMultiple = inputType === 'multiple_choice';
                  const isDropdown = inputType === 'dropdown';
                  const isMultiText = inputType === 'multi_text';
                  const displayValue = Array.isArray(value) ? value.join(', ') : (value ?? '');
                  return (
                    <div key={variable.variable_name}>
                      <label className="block text-sm font-medium mb-2">
                        {variable.display_name || variable.variable_name}
                      </label>
                      <p className="text-xs text-gray-400 mb-2">
                        {variable.description || 'No description provided.'}
                      </p>
                      {isDropdown && (
                        <input
                          className="input-glass"
                          value={displayValue}
                          readOnly
                        />
                      )}
                      {isMultiple && (
                        <input
                          className="input-glass"
                          value={displayValue}
                          readOnly
                        />
                      )}
                      {!isDropdown && !isMultiple && (
                        <>
                          {isMultiText ? (
                            <textarea
                              value={displayValue}
                              className="input-glass"
                              rows="3"
                              readOnly
                            />
                          ) : (
                            <input
                              type={inputType === 'number' ? 'number' : 'text'}
                              value={displayValue}
                              className="input-glass"
                              readOnly
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={Boolean(deleteTarget)}
        title="Delete Saved Prompt"
        onClose={() => setDeleteTarget(null)}
        footer={(
          <>
            <button className="btn-secondary" onClick={() => setDeleteTarget(null)}>Cancel</button>
            <button className="btn-primary danger" onClick={handleDeleteSave}>Delete</button>
          </>
        )}
      >
        <p className="text-gray-400">Are you sure you want to delete this saved prompt?</p>
      </Modal>
    </div>
  );
}

export default SavedPromptsPage;
